/**
 * lib/datasets/guides-data.ts
 *
 * Central data registry for 2026 home improvement costs, material lifespans, and ranges.
 * By placing these dynamic parameters here, the written guides can reference them
 * programmatically, ensuring simple auditing and updates.
 *
 * Last verified: July 1, 2026
 */

export const PRICING_2026 = {
  roofing: {
    averageTotalCost: { min: 8500, max: 22000 },
    costPerSqFt: {
      asphalt: { min: 4.50, max: 7.00 },
      metal: { min: 8.00, max: 16.00 },
      tile: { min: 12.00, max: 25.00 },
      slate: { min: 15.00, max: 35.00 },
    },
    lifespanYears: {
      asphalt: { min: 15, max: 30 },
      metal: { min: 40, max: 70 },
      tile: { min: 50, max: 100 },
      slate: { min: 75, max: 200 },
    }
  },
  hvac: {
    replacementCost: {
      average: { min: 7500, max: 12500 },
      centralAc: { min: 6000, max: 12500 },
      miniSplit: { min: 4500, max: 14000 },
      heatPump: { min: 7000, max: 16000 },
      furnace: { min: 3000, max: 7500 },
    },
    lifespanYears: {
      centralAc: { min: 12, max: 15 },
      heatPump: { min: 10, max: 15 },
      miniSplit: { min: 15, max: 20 },
    }
  },
  kitchen: {
    remodelCost: {
      budget: { min: 12000, max: 25000 },
      midRange: { min: 25000, max: 65000 },
      luxury: { min: 65000, max: 130000 },
    }
  },
  bathroom: {
    remodelCost: {
      budget: { min: 5000, max: 12000 },
      midRange: { min: 12000, max: 28000 },
      luxury: { min: 28000, max: 60000 },
    }
  },
  solar: {
    grossCostRange: {
      average: { min: 18000, max: 25000 },
    },
    federalItcPercentage: 30, // 30% tax credit
    paybackPeriodYears: { min: 6, max: 9 },
  },
  windows: {
    replacementCostPerWindow: {
      average: { min: 650, max: 1300 },
      vinyl: { min: 450, max: 900 },
      woodFiberglass: { min: 900, max: 1800 },
    }
  },
  deck: {
    costPerSqFtInstalled: {
      woodTreated: { min: 15, max: 35 },
      composite: { min: 35, max: 65 },
      hardwoodIpe: { min: 45, max: 80 },
    }
  },
  concrete: {
    bagCost80lb: { min: 6.00, max: 8.00 },
    deliveryPerCubicYard: { min: 140, max: 180 },
  },
  paint: {
    gallonCost: { min: 45, max: 75 },
    coverageSqFtPerGallon: 400,
  },
  flooring: {
    costPerSqFtInstalled: {
      vinylLVP: { min: 4.00, max: 8.00 },
      laminate: { min: 5.00, max: 9.00 },
      hardwood: { min: 9.00, max: 18.00 },
    }
  },
  fence: {
    costPerLinearFootInstalled: {
      wood: { min: 18, max: 35 },
      vinyl: { min: 25, max: 45 },
      metal: { min: 30, max: 65 },
    }
  },
  waterHeater: {
    replacementCost: {
      tank: { min: 1200, max: 2500 },
      tankless: { min: 2500, max: 4500 },
    },
    lifespanYears: {
      tank: { min: 8, max: 12 },
      tankless: { min: 15, max: 20 },
    }
  },
  roi: {
    kitchenRemodel: {
      budget: { min: 75, max: 82 },
      midRange: { min: 55, max: 62 },
    },
    deckAddition: {
      wood: { min: 65, max: 72 },
    }
  },
  insulation: {
    costPerSqFt: {
      fiberglass: { min: 1.00, max: 1.80 },
      cellulose: { min: 1.20, max: 2.00 },
      sprayFoam: { min: 2.20, max: 4.50 },
    }
  },
  smartThermostat: {
    costInstalled: { min: 150, max: 400 },
    averageSavingsPercent: { heating: 10, cooling: 15 },
  },
  pool: {
    costInstalled: {
      aboveGround: { min: 3000, max: 10000 },
      inGround: { min: 45000, max: 85000 },
    }
  },
  sodLawn: {
    costPerSqFt: {
      sod: { min: 1.00, max: 2.50 },
      seed: { min: 0.10, max: 0.40 },
    }
  },
  gutters: {
    costPerLinearFootInstalled: {
      vinyl: { min: 4.00, max: 8.00 },
      seamlessAluminum: { min: 8.00, max: 15.00 },
      copper: { min: 25.00, max: 40.00 },
    }
  },
  siding: {
    costPerSqFtInstalled: {
      vinyl: { min: 4.00, max: 8.00 },
      fiberCement: { min: 8.00, max: 14.00 },
      wood: { min: 9.00, max: 16.00 },
    }
  },
  drivewaySealing: {
    costPerSqFtInstalled: { min: 0.20, max: 0.50 },
  },
  garageDoorWindRated: {
    costInstalled: {
      single: { min: 1000, max: 2500 },
      double: { min: 2000, max: 4500 },
    }
  },
  basementWaterproofing: {
    averageSystemCost: { min: 3500, max: 10000 },
  }
};
